import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import ShareButton from "@/components/ShareButton";
import { Award, ArrowLeft, Trophy } from "lucide-react";

const CATEGORY_LABELS = {
  overall: "Overall", offensive: "Offensive", defensive: "Defensive",
  athletic: "Athletic", pitching: "Pitching", position: "Position",
};
const CATEGORY_ORDER = ["overall", "offensive", "defensive", "athletic", "pitching", "position"];

export default function CampAwards() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/camps/${slug}/awards`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PublicLayout><div className="max-w-5xl mx-auto p-10 label-overline">Loading awards…</div></PublicLayout>;
  if (notFound || !data) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto p-10">
          <div className="font-display text-3xl uppercase">Camp not found</div>
          <Link to="/camps" className="text-[#E63946] text-sm underline mt-3 inline-block">← All camps</Link>
        </div>
      </PublicLayout>
    );
  }

  const camp = data.camp;
  const grouped = data.grouped || {};

  return (
    <PublicLayout>
      <section className="relative bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${camp.cover_image_url || ""})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-14">
          <Link to={`/camps/${camp.slug}/results`} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-wider"><ArrowLeft className="w-4 h-4" /> Camp Results</Link>
          <div className="flex items-center gap-2 label-overline text-[#E63946] mt-4"><Award className="w-4 h-4" /> Camp Awards</div>
          <h1 data-testid="camp-awards-title" className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-2">{camp.title}</h1>
          <p className="text-white/60 text-sm mt-2">{[camp.city, camp.province].filter(Boolean).join(", ")}</p>
          <div className="mt-5"><ShareButton url={`${window.location.origin}/camps/${camp.slug}/awards`} title={`${camp.title} Award Winners · CBR Prospects`} /></div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {data.count === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-sm p-12 text-center">
              <Trophy className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Award winners for this camp haven't been published yet. Check back soon.</p>
            </div>
          ) : (
            <div className="space-y-10">
              {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
                <section key={cat} data-testid={`camp-awards-${cat}`}>
                  <h2 className="font-display text-2xl uppercase tracking-tight text-[#0A192F]">{CATEGORY_LABELS[cat]}</h2>
                  <div className="mt-4 bg-white border border-slate-200 rounded-sm overflow-hidden">
                    {grouped[cat].map((a, i) => (
                      <div key={a.id} data-testid={`camp-award-row-${a.award_key}`} className={`flex items-center gap-4 p-4 ${i > 0 ? "border-t border-slate-100" : ""}`}>
                        <div className="w-10 h-10 rounded-sm bg-[#E63946]/10 text-[#E63946] grid place-items-center shrink-0"><Award className="w-5 h-5" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="label-overline text-[#E63946] text-[10px]">{a.award_label}</div>
                          <Link to={`/prospects/${a.player_slug}`} className="font-semibold hover:text-[#E63946] block truncate">{a.player_name}</Link>
                          <div className="text-[11px] text-slate-500">{[a.player_position, a.player_grad_year].filter(Boolean).join(" · ")}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-data text-2xl text-[#0A192F] leading-none">{a.stat_value}{a.stat_units ? <span className="text-sm text-slate-400"> {a.stat_units}</span> : ""}</div>
                          <div className="text-[9px] uppercase tracking-wider text-slate-400 mt-0.5">{a.stat_label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
